/**
 * KPI TanStack Query Hooks
 *
 * Provides hooks for fetching and mutating KPI data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getKpiDefinitions,
  getKpiDefinition,
  createKpiDefinition,
  updateKpiDefinition,
  deleteKpiDefinition,
  getKpiValues,
  getKpiValue,
  createKpiValue,
  upsertKpiValue,
  createKpiValuesBulk,
  updateKpiValue,
  archiveKpiValue,
  clearKpiPeriod,
  getKpiScorecard,
  getKpiCompletionStats,
} from '../api';
import type {
  KpiDefinitionFilters,
  KpiValueFilters,
  ScorecardFilters,
  CompletionStatsFilters,
  CreateKpiDefinitionInput,
  UpdateKpiDefinitionInput,
  CreateKpiValueInput,
  CreateKpiValuesBulkInput,
  UpdateKpiValueInput,
} from '../types';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Query Keys Factory
// =============================================================================

export const kpiKeys = {
  all: ['kpis'] as const,
  // Definitions - use definitionsAll for invalidation to match all filtered queries
  definitionsAll: () => [...kpiKeys.all, 'definitions'] as const,
  definitions: (filters?: KpiDefinitionFilters) => [...kpiKeys.all, 'definitions', filters] as const,
  definition: (id: string) => [...kpiKeys.all, 'definition', id] as const,
  // Values - use valuesAll for invalidation to match all filtered queries
  valuesAll: () => [...kpiKeys.all, 'values'] as const,
  values: (filters?: KpiValueFilters) => [...kpiKeys.all, 'values', filters] as const,
  value: (id: string) => [...kpiKeys.all, 'value', id] as const,
  valuesByKpiAll: (kpiId: string) => [...kpiKeys.all, 'values-by-kpi', kpiId] as const,
  valuesByKpi: (kpiId: string, filters?: KpiValueFilters) =>
    [...kpiKeys.all, 'values-by-kpi', kpiId, filters] as const,
  // Scorecard - use scorecardAll for invalidation to match all filtered queries
  scorecardAll: () => [...kpiKeys.all, 'scorecard'] as const,
  scorecard: (filters?: ScorecardFilters) => [...kpiKeys.all, 'scorecard', filters] as const,
  // Completion Stats
  completionStats: (filters?: CompletionStatsFilters) => [...kpiKeys.all, 'completion-stats', filters] as const,
};

// =============================================================================
// KPI Definition Hooks
// =============================================================================

/**
 * Hook to fetch KPI definitions with optional filters
 */
export function useKpiDefinitions(filters?: KpiDefinitionFilters) {
  return useQuery({
    queryKey: kpiKeys.definitions(filters),
    queryFn: () => getKpiDefinitions(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch a single KPI definition
 */
export function useKpiDefinition(id: string) {
  return useQuery({
    queryKey: kpiKeys.definition(id),
    queryFn: () => getKpiDefinition(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * Hook to create a new KPI definition
 */
export function useCreateKpiDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateKpiDefinitionInput) => createKpiDefinition(data),
    onSuccess: (definition) => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.definitionsAll() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.scorecardAll() });
      toast.success(`KPI "${definition.name}" created successfully`);
    },
    onError: (error) => {
      toast.error('Failed to create KPI', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to update a KPI definition
 */
export function useUpdateKpiDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateKpiDefinitionInput }) =>
      updateKpiDefinition(id, data),
    onSuccess: (definition, variables) => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.definitionsAll() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.definition(variables.id) });
      queryClient.invalidateQueries({ queryKey: kpiKeys.scorecardAll() });
      toast.success(`KPI "${definition.name}" updated successfully`);
    },
    onError: (error) => {
      toast.error('Failed to update KPI', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to delete (deactivate) a KPI definition
 */
export function useDeleteKpiDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteKpiDefinition(id),
    onSuccess: (_result, id) => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.definitionsAll() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.definition(id) });
      queryClient.invalidateQueries({ queryKey: kpiKeys.scorecardAll() });
      toast.success('KPI deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete KPI', { description: getErrorMessage(error) });
    },
  });
}

// =============================================================================
// KPI Value Hooks
// =============================================================================

/**
 * Hook to fetch KPI values with optional filters
 */
export function useKpiValues(filters?: KpiValueFilters) {
  return useQuery({
    queryKey: kpiKeys.values(filters),
    queryFn: () => getKpiValues(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to fetch KPI values for a specific KPI
 */
export function useKpiValuesByKpi(kpiId: string, filters?: Omit<KpiValueFilters, 'kpi_id'>) {
  return useQuery({
    queryKey: kpiKeys.valuesByKpi(kpiId, filters),
    queryFn: () => getKpiValues({ ...filters, kpi_id: kpiId }),
    enabled: !!kpiId,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to fetch a single KPI value
 */
export function useKpiValue(id: string) {
  return useQuery({
    queryKey: kpiKeys.value(id),
    queryFn: () => getKpiValue(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to create a new KPI value
 */
export function useCreateKpiValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateKpiValueInput) => createKpiValue(data),
    onSuccess: (value) => {
      // Don't invalidate scorecard - it reads from snapshots computed by scheduler
      queryClient.invalidateQueries({ queryKey: kpiKeys.valuesAll() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.valuesByKpiAll(value.kpi_id) });
      toast.success('Value recorded successfully');
    },
    onError: (error) => {
      toast.error('Failed to record value', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to create multiple KPI values at once
 */
export function useCreateKpiValuesBulk() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateKpiValuesBulkInput) => createKpiValuesBulk(data),
    onSuccess: (values) => {
      // Don't invalidate scorecard - it reads from snapshots computed by scheduler
      if (values.length > 0) {
        queryClient.invalidateQueries({ queryKey: kpiKeys.valuesAll() });
        queryClient.invalidateQueries({ queryKey: kpiKeys.valuesByKpiAll(values[0].kpi_id) });
      }
      toast.success(`${values.length} values recorded successfully`);
    },
    onError: (error) => {
      toast.error('Failed to record values', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to update a KPI value
 */
export function useUpdateKpiValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateKpiValueInput }) =>
      updateKpiValue(id, data),
    onSuccess: (value, variables) => {
      // Don't invalidate scorecard - it reads from snapshots computed by scheduler
      queryClient.invalidateQueries({ queryKey: kpiKeys.valuesAll() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.value(variables.id) });
      queryClient.invalidateQueries({ queryKey: kpiKeys.valuesByKpiAll(value.kpi_id) });
      toast.success('Value updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update value', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to archive a KPI value
 */
export function useArchiveKpiValue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: string; kpiId: string }) => archiveKpiValue(id),
    onSuccess: (_result, variables) => {
      // Don't invalidate scorecard - it reads from snapshots computed by scheduler
      queryClient.invalidateQueries({ queryKey: kpiKeys.valuesAll() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.value(variables.id) });
      queryClient.invalidateQueries({ queryKey: kpiKeys.valuesByKpiAll(variables.kpiId) });
      toast.success('Value archived successfully');
    },
    onError: (error) => {
      toast.error('Failed to archive value', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to clear a scorecard cell (archive all values in the period).
 * The server recomputes the snapshot so average/total skip the cleared cell.
 */
export function useClearKpiPeriod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { kpiId: string; periodStart: string; periodEnd: string }) =>
      clearKpiPeriod({
        kpi_id: input.kpiId,
        period_start: input.periodStart,
        period_end: input.periodEnd,
      }),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: kpiKeys.scorecardAll() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.valuesAll() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.valuesByKpiAll(variables.kpiId) });
      toast.success('Cell cleared');
    },
    onError: (error) => {
      toast.error('Failed to clear cell', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to create a KPI value with optimistic updates for the scorecard.
 * Uses upsert endpoint which replaces existing values in the same period.
 * Provides instant feedback while saving, with rollback on error.
 */
export function useCreateKpiValueOptimistic(scorecardFilters?: ScorecardFilters) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateKpiValueInput) => upsertKpiValue(data),
    onMutate: async (newValue) => {
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: kpiKeys.scorecard(scorecardFilters) });

      // Snapshot previous value for rollback
      const previousScorecard = queryClient.getQueryData<import('../types').ScorecardResponse>(
        kpiKeys.scorecard(scorecardFilters)
      );

      // Optimistically update the scorecard
      if (previousScorecard) {
        queryClient.setQueryData<import('../types').ScorecardResponse>(
          kpiKeys.scorecard(scorecardFilters),
          (old) => {
            if (!old) return old;

            return {
              ...old,
              categories: old.categories.map((category) => ({
                ...category,
                kpis: category.kpis.map((kpi) => {
                  if (kpi.id !== newValue.kpi_id) return kpi;

                  // Find the period this value belongs to
                  const valueDate = new Date(newValue.datetime);
                  const updatedValues = kpi.values.map((pv) => {
                    const periodStart = new Date(pv.period_start);
                    const periodEnd = new Date(pv.period_end);

                    if (valueDate >= periodStart && valueDate <= periodEnd) {
                      // UPSERT: Replace the value (not accumulate)
                      // Since we're replacing, count stays at 1 and all aggregates equal the new value
                      return {
                        ...pv,
                        value: newValue.value,
                        value_sum: newValue.value,
                        value_avg: newValue.value,
                        value_count: 1,
                        // Recalculate on_track based on goal
                        on_track: calculateOnTrack(kpi, newValue.value),
                        notes: newValue.notes ?? pv.notes ?? null,
                      };
                    }
                    return pv;
                  });

                  return {
                    ...kpi,
                    current_value: newValue.value,
                    values: updatedValues,
                  };
                }),
              })),
            };
          }
        );
      }

      return { previousScorecard };
    },
    onError: (error, _variables, context) => {
      // Rollback to previous state on error
      if (context?.previousScorecard) {
        queryClient.setQueryData(
          kpiKeys.scorecard(scorecardFilters),
          context.previousScorecard
        );
      }
      toast.error('Failed to save value', { description: getErrorMessage(error) });
    },
    onSuccess: (value) => {
      // Only invalidate the values queries - NOT the scorecard or completion stats
      // Both scorecard and completion stats read from kpi_snapshots which are computed
      // by a scheduler job. Invalidating them would refetch stale snapshot data and
      // overwrite the optimistic update we just applied. The optimistic cache update
      // already has the correct data, so we keep it until snapshots are refreshed.
      queryClient.invalidateQueries({ queryKey: kpiKeys.valuesAll() });
      queryClient.invalidateQueries({ queryKey: kpiKeys.valuesByKpiAll(value.kpi_id) });
      toast.success('Value saved');
    },
  });
}

/**
 * Helper function to calculate on_track status based on KPI goal settings
 */
function calculateOnTrack(
  kpi: { goal_value: number | null; goal_min: number | null; goal_max: number | null; orientation_rule: string },
  value: number
): boolean | null {
  if (kpi.goal_value === null && kpi.goal_min === null && kpi.goal_max === null) {
    return null;
  }

  switch (kpi.orientation_rule) {
    case 'gte_goal':
      return kpi.goal_value !== null ? value >= kpi.goal_value : null;
    case 'lte_goal':
      return kpi.goal_value !== null ? value <= kpi.goal_value : null;
    case 'eq_goal':
      return kpi.goal_value !== null ? value === kpi.goal_value : null;
    case 'inside_range':
      return kpi.goal_min !== null && kpi.goal_max !== null
        ? value >= kpi.goal_min && value <= kpi.goal_max
        : null;
    case 'outside_range':
      return kpi.goal_min !== null && kpi.goal_max !== null
        ? value < kpi.goal_min || value > kpi.goal_max
        : null;
    default:
      return null;
  }
}

// =============================================================================
// KPI Scorecard Hooks
// =============================================================================

/**
 * Hook to fetch the KPI scorecard
 */
export function useKpiScorecard(filters?: ScorecardFilters) {
  return useQuery({
    queryKey: kpiKeys.scorecard(filters),
    queryFn: () => getKpiScorecard(filters),
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 30, // Poll every 30 seconds for real-time updates across users
  });
}

// =============================================================================
// KPI Completion Stats Hooks
// =============================================================================

/**
 * Hook to fetch KPI completion statistics for a period.
 * Used by the completion tracking widget to show org-wide completion status.
 */
export function useKpiCompletionStats(filters?: CompletionStatsFilters) {
  return useQuery({
    queryKey: kpiKeys.completionStats(filters),
    queryFn: () => getKpiCompletionStats(filters),
    enabled: !!filters?.period_type, // Only fetch if period_type is provided
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}
